


import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Container, Grid, Card, CardContent, Typography, Paper, Avatar,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Button, Alert, Tooltip, TextField, ButtonGroup, Stack,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, List, ListItem, ListItemIcon, ListItemText, ListItemButton,
  TablePagination, Select, MenuItem, FormControl, InputLabel, Snackbar,
  LinearProgress, Accordion, AccordionSummary, AccordionDetails,
  ToggleButtonGroup, ToggleButton, InputAdornment, Badge, Collapse
} from '@mui/material';
import {
  AssignmentReturn, Inventory, Payments, Search as SearchIcon, Close as CloseIcon,
  ArrowForward, Schedule, ErrorOutline, CheckCircle, EventAvailable, QrCodeScanner,
  Add as AddIcon, DeleteOutline, Groups, InfoOutlined, Layers as LayersIcon,
  Refresh as RefreshIcon, ExpandMore as ExpandMoreIcon, WarningAmber,
  ReceiptLong, LocalShipping, FilterList
} from '@mui/icons-material';

// Config / constants

const API_BASE = process.env.REACT_APP_VENDER_RETURN_API_URL;
const MEDICINE_API_BASE = process.env.REACT_APP_MEDICINE_API_URL;
const DISTRIBUTOR_API_BASE = process.env.REACT_APP_DISTRIBUTOR_API_URL;

const COLORS = {
  primary: '#006D77',
  primaryDark: '#005761',
  primaryBg: '#006D7710',
  success: '#52B788',
  successBg: '#52B78815',
  warning: '#F77F00',
  warningBg: '#F77F0015',
  error: '#EF476F',
  errorBg: '#EF476F15',
  info: '#2E86AB',
  infoBg: '#2E86AB15',
  grey: '#8892a0',
};

const REASONS = [
  { value: 'expired', label: 'Expired' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'excess_stock', label: 'Excess stock' },
  { value: 'recall', label: 'Recall' },
  { value: 'quality_issue', label: 'Quality issue' },
];

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'credit_note_adjustment', label: 'Credit note adjustment' },
];

// Helpers

function money(n) {
  const v = Number(n || 0);
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDateTime(iso) {
  if (!iso) return 'N/A';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
}

function statusMeta(settlementStatus) {
  if (settlementStatus === 'fully_settled') {
    return { label: 'Fully settled', color: COLORS.success, bg: COLORS.successBg, icon: CheckCircle };
  }
  if (settlementStatus === 'partially_settled') {
    return { label: 'Partially settled', color: COLORS.info, bg: COLORS.infoBg, icon: Schedule };
  }
  return { label: 'Pending settlement', color: COLORS.warning, bg: COLORS.warningBg, icon: ErrorOutline };
}

function daysColor(days) {
  if (days <= 10) return COLORS.error;
  if (days <= 30) return COLORS.warning;
  return COLORS.success;
}

async function apiFetch(path, options = {}, base = API_BASE) {
  const token = sessionStorage.getItem('token');
  const res = await fetch(`${base}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  return data;
}

// Small shared components

function StatCard({ title, value, icon, color, subtitle }) {
  return (
    <Card sx={{
      height: '100%',
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      border: `1px solid ${color}30`,
      transition: 'all 0.25s ease',
      '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 8px 20px ${color}20` },
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: subtitle ? 1 : 0 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
            <Typography variant="h5" fontWeight="bold" color={color}>{value}</Typography>
          </Box>
          <Avatar sx={{ bgcolor: color, width: 44, height: 44 }}>{icon}</Avatar>
        </Box>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </CardContent>
    </Card>
  );
}

function BalanceBar({ total, settled, height = 8 }) {
  const pct = total > 0 ? Math.min(100, (settled / total) * 100) : 0;
  const color = pct >= 100 ? COLORS.success : pct > 0 ? COLORS.info : COLORS.warning;
  return (
    <Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height, borderRadius: height / 2, bgcolor: '#EAEAEA',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: height / 2 },
        }}
      />
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">{money(settled)} settled</Typography>
        <Typography variant="caption" color="text.secondary">{money(Math.max(0, total - settled))} left</Typography>
      </Stack>
    </Box>
  );
}

function ReasonChip({ reason }) {
  const label = reason === 'mixed' ? 'Mixed reasons' : (REASONS.find((r) => r.value === reason)?.label || reason || '—');
  return <Chip size="small" label={label} variant="outlined" sx={{ fontWeight: 600, textTransform: 'capitalize' }} />;
}

function MedicineFactsChips({ m, compact }) {
  const activeBatchCount = (m.batches || []).filter((b) => b.is_active && b.quantity > 0).length;
  const gstRate = (m.igst && m.igst > 0) ? m.igst : ((m.cgst || 0) + (m.sgst || 0));
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: compact ? 0.75 : 1.25 }}>
      <Chip size="small" icon={<LayersIcon sx={{ fontSize: 14 }} />} label={`Stock ${m.stock ?? '—'}`}
        color={m.stock > 0 ? 'default' : 'error'} variant="outlined" />
      <Chip size="small" label={`${activeBatchCount} active batch${activeBatchCount === 1 ? '' : 'es'}`} variant="outlined" />
      {m.category && <Chip size="small" label={m.category} variant="outlined" />}
      {m.hsn_code && <Chip size="small" label={`HSN ${m.hsn_code}`} variant="outlined" />}
      {m.schedule && <Chip size="small" label={`Schedule ${m.schedule}`} color="warning" variant="outlined" />}
      {gstRate > 0 && <Chip size="small" label={`GST ${gstRate.toFixed(1)}%`} variant="outlined" />}
      {(m.distributor_names || []).length > 0 && (
        <Chip size="small" icon={<Groups sx={{ fontSize: 14 }} />}
          label={`${m.distributor_names.length} distributor${m.distributor_names.length === 1 ? '' : 's'}`} variant="outlined" />
      )}
      {m.prescription_required && <Chip size="small" color="error" label="Rx required" variant="outlined" />}
    </Stack>
  );
}

// Near-expiry / expired stock panel

function NearExpiryPanel({ onAddToCart }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(10);
  const [customDays, setCustomDays] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const presets = [10, 30, 60, 90];
  const isCustomActive = !presets.includes(windowDays);

  const load = useCallback(async (days) => {
    setLoading(true);
    setErrorMsg('');
    try {
      const data = await apiFetch(`/medicines/near-expiry?days=${days}&limit=50`);
      setBatches(data.batches || []);
    } catch (e) {
      setBatches([]);
      setErrorMsg(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(windowDays); }, [windowDays, load]);

  const applyCustomDays = () => {
    const n = parseInt(customDays, 10);
    if (!Number.isFinite(n) || n < 0) return;
    setWindowDays(n);
  };


  const filteredBatches = searchTerm.trim()
    ? batches.filter((b) => {
        const term = searchTerm.trim().toLowerCase();
        return (
          (b.medicine_name || '').toLowerCase().includes(term) ||
          (b.batch_number || '').toLowerCase().includes(term) ||
          (b.manufacturer || '').toLowerCase().includes(term)
        );
      })
    : batches;

  return (
    <Paper elevation={1} sx={{ borderRadius: 3, mb: 3, overflow: 'hidden' }}>
      <Box
        sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: COLORS.primaryBg, color: COLORS.primary, width: 36, height: 36 }}>
            <EventAvailable fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color={COLORS.primary}>Expired &amp; nearing expiry</Typography>
            <Typography variant="caption" color="text.secondary">
              Tap a batch to start a return for it
            </Typography>
          </Box>
        </Stack>
        <ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: '0.2s', color: 'text.secondary' }} />
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <ButtonGroup size="small">
            {presets.map((d) => (
              <Button
                key={d}
                variant={windowDays === d ? 'contained' : 'outlined'}
                onClick={() => { setCustomDays(''); setWindowDays(d); }}
                sx={windowDays === d ? { bgcolor: COLORS.primary, '&:hover': { bgcolor: COLORS.primaryDark } } : { borderColor: COLORS.primary, color: COLORS.primary }}
              >
                {d}d
              </Button>
            ))}
          </ButtonGroup>
          <TextField
            size="small"
            type="number"
            placeholder="Custom days"
            value={customDays}
            onChange={(e) => setCustomDays(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyCustomDays()}
            sx={{ width: 150 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={applyCustomDays}><SearchIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ),
            }}
          />
          {isCustomActive && (
            <Typography variant="caption" color={COLORS.primary}>
              Showing batches within {windowDays} day{windowDays === 1 ? '' : 's'}
            </Typography>
          )}
          <TextField
            size="small"
            placeholder="Search medicine, batch, or manufacturer…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 820, ml: { sm: 'auto' } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        <Divider />

        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} sx={{ color: COLORS.primary }} /></Box>
        ) : errorMsg ? (
          <Alert severity="error" sx={{ m: 2 }}>{errorMsg}</Alert>
         ) : filteredBatches.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
           <Typography variant="body2">
              {searchTerm.trim()
                ? `No matches for "${searchTerm.trim()}" in this window.`
             : `Nothing expired or expiring within ${windowDays} days.`}
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ maxHeight: 280, overflowY: 'auto' }}>
            {filteredBatches.map((b, idx) => (
              <ListItemButton
                key={b.batch_id}
                divider={idx < filteredBatches.length - 1}
                onClick={() => onAddToCart(b)}
              >
                <ListItemText
                  primary={<Typography variant="body2" fontWeight={600}>{b.medicine_name}</Typography>}
                  secondary={`Batch ${b.batch_number} · Qty ${b.quantity} · Base price ${money(b.base_price)}`}
                />
                <Box sx={{ textAlign: 'right', ml: 2 }}>
                  <Typography variant="body2" fontWeight={700} sx={{ color: daysColor(b.days_to_expiry) }}>
                    {b.is_expired ? `Expired ${Math.abs(b.days_to_expiry)}d ago` : `${b.days_to_expiry}d left`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{b.expiry_date}</Typography>
                  <Chip size="small" icon={<AddIcon sx={{ fontSize: 13 }} />} label="Add to return" sx={{ mt: 0.5, height: 20, fontSize: 10.5 }} />
                </Box>
              </ListItemButton>
            ))}
          </List>
        )}
      </Collapse>
    </Paper>
  );
}

// Distributor picker

function DistributorField({
  distributors, loading, selectedId, selectedName, onSelect, onFreeText,
  allDistributors, allDistributorsLoading,
}) {
  if (loading) {
    return <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /><Typography variant="body2" color="text.secondary">Loading distributors…</Typography></Stack>;
  }

  if (!distributors || distributors.length === 0) {

    if (allDistributorsLoading) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">Loading your distributors…</Typography>
        </Stack>
      );
    }

    if (allDistributors && allDistributors.length > 0) {
      return (
        <Box>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
            <Groups fontSize="small" color="disabled" />
            <Typography variant="caption" color="text.secondary">
              Not linked to this medicine yet — pick a distributor
            </Typography>
          </Stack>
          <FormControl fullWidth size="small">
            <InputLabel>Distributor</InputLabel>
            <Select
              label="Distributor"
              value={selectedId || ''}
              onChange={(e) => {
                const match = allDistributors.find((d) => d.distributor_id === e.target.value);
                onSelect(match ? { distributor_id: match.distributor_id, company_name: match.company_name || match.name } : null);
              }}
            >
              {allDistributors.map((d) => (
                <MenuItem key={d.distributor_id} value={d.distributor_id}>
                  {d.company_name || d.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      );
    }

    return (
      <Box>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
          <Groups fontSize="small" color="disabled" />
          <Typography variant="caption" color="text.secondary">No distributors found — enter manually</Typography>
        </Stack>
        <TextField
          fullWidth size="small" value={selectedName || ''}
          onChange={(e) => onFreeText(e.target.value)}
          placeholder="Distributor name (must match an existing record)"
        />
      </Box>
    );
  }

  if (distributors.length === 1) {
    const d = distributors[0];
    return (
      <Paper variant="outlined" sx={{ p: 1.25, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#FAFAFA' }}>
        <Groups fontSize="small" sx={{ color: COLORS.primary }} />
        <Typography variant="body2" fontWeight={600}>{d.company_name}</Typography>
        {d.unresolved && <Chip size="small" color="error" label="not in master" />}
      </Paper>
    );
  }

  return (
    <FormControl fullWidth size="small">
      <InputLabel>Distributor</InputLabel>
      <Select
        label="Distributor"
        value={selectedId || selectedName || ''}
        onChange={(e) => {
          const match = distributors.find((d) => (d.distributor_id || d.company_name) === e.target.value);
          onSelect(match);
        }}
      >
        {distributors.map((d) => (
          <MenuItem key={d.distributor_id || d.company_name} value={d.distributor_id || d.company_name}>
            {d.company_name}{d.unresolved ? ' (unresolved)' : ''}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// Raise return dialog (search / batch lookup / cart / bulk submit)

function CreateReturnDialog({ open, onClose, onCreated, quickAddBatch, notify }) {
  const [mode, setMode] = useState('name');
  const [query, setQuery] = useState('');
  const [medicines, setMedicines] = useState([]);
  const [searching, setSearching] = useState(false);

  const [batchQuery, setBatchQuery] = useState('');
  const [batchSearching, setBatchSearching] = useState(false);
  const [batchError, setBatchError] = useState('');
  const [batchMatches, setBatchMatches] = useState([]);

  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const [distributors, setDistributors] = useState([]);
  const [distributorsLoading, setDistributorsLoading] = useState(false);
  const [selectedDistributorId, setSelectedDistributorId] = useState(null);
  const [selectedDistributorName, setSelectedDistributorName] = useState('');
  const [allDistributors, setAllDistributors] = useState([]);
  const [allDistributorsLoading, setAllDistributorsLoading] = useState(false);
  const allDistributorsFetchedRef = useRef(false);
  const [cart, setCart] = useState([]);
  const [rowError, setRowError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const debounceRef = useRef(null);
  const batchDebounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) { setMedicines([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await apiFetch(`/medicines?search=${encodeURIComponent(query)}&limit=15`, {}, MEDICINE_API_BASE);
        setMedicines(data.medicines || []);
      } catch (e) { setMedicines([]); } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  useEffect(() => {
    if (batchDebounceRef.current) clearTimeout(batchDebounceRef.current);
    setBatchError(''); setBatchMatches([]);
    if (!batchQuery || batchQuery.length < 3) return;
    batchDebounceRef.current = setTimeout(async () => {
      setBatchSearching(true);
      try {
        const data = await apiFetch(`/medicines/batch-lookup?batch_number=${encodeURIComponent(batchQuery)}`);
        const matches = data.matches || [];
        if (matches.length === 1) { setSelectedMedicine(matches[0].medicine); setSelectedBatch(matches[0].matched_batch); }
        else if (matches.length > 1) { setBatchMatches(matches); }
        else { setBatchError('No batch found matching that ID/number.'); }
      } catch (e) { setBatchError(e.message || 'No batch found matching that ID/number.'); }
      finally { setBatchSearching(false); }
    }, 350);
    return () => clearTimeout(batchDebounceRef.current);
  }, [batchQuery]);

 useEffect(() => {

  if (
    selectedMedicine &&
    !distributorsLoading &&
    distributors.length === 0 &&
    !allDistributorsFetchedRef.current
  ) {
    allDistributorsFetchedRef.current = true;
     setAllDistributorsLoading(true);
     apiFetch('/dropdown', {}, DISTRIBUTOR_API_BASE)
       .then((data) => setAllDistributors(Array.isArray(data) ? data : []))
       .catch(() => setAllDistributors([]))
       .finally(() => setAllDistributorsLoading(false));
   }
   }, [selectedMedicine, distributors, distributorsLoading]);


useEffect(() => {
  if (!open) return;

}, [open]);


  useEffect(() => {
    if (!quickAddBatch) return;
    setSelectedMedicine({
      medicine_id: quickAddBatch.medicine_id, name: quickAddBatch.medicine_name,
      manufacturer: quickAddBatch.manufacturer,
      batches: [{
        batch_id: quickAddBatch.batch_id, batch_number: quickAddBatch.batch_number,
        quantity: quickAddBatch.quantity, mrp: quickAddBatch.mrp, base_price: quickAddBatch.base_price,
        expiry_date: quickAddBatch.expiry_date, manufacture_date: quickAddBatch.manufacture_date, is_active: true,
      }],
    });
    setSelectedBatch({
      batch_id: quickAddBatch.batch_id, batch_number: quickAddBatch.batch_number,
      quantity: quickAddBatch.quantity, mrp: quickAddBatch.mrp, base_price: quickAddBatch.base_price,
      expiry_date: quickAddBatch.expiry_date, manufacture_date: quickAddBatch.manufacture_date, is_active: true,
    });
    setQuantity(String(quickAddBatch.quantity));
    setReason(quickAddBatch.is_expired ? 'expired' : 'excess_stock');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickAddBatch]);

  const activeBatches = (selectedMedicine?.batches || []).filter((b) => b.is_active && b.quantity > 0);

  useEffect(() => {
    if (!selectedMedicine) return;
    const active = (selectedMedicine.batches || []).filter((b) => b.is_active && b.quantity > 0);
    if (active.length === 1) setSelectedBatch(active[0]);
  }, [selectedMedicine]);

  const resetPicker = () => {
    setSelectedMedicine(null); setSelectedBatch(null); setQuantity(''); setReason(''); setNotes('');
    setBatchQuery(''); setBatchMatches([]); setBatchError(''); setQuery(''); setMedicines([]);
    setDistributors([]); setSelectedDistributorId(null); setSelectedDistributorName('');
  };

  const resetAll = () => { resetPicker(); setCart([]); setSubmitError(''); setRowError(''); setMode('name'); };

  const handleAddToCart = () => {
    setRowError('');
    if (!selectedMedicine) return setRowError('Select a medicine first.');
    if (!selectedBatch) return setRowError('Select a batch to return.');
    if (!quantity || Number(quantity) <= 0) return setRowError('Enter a valid quantity (minimum 1 unit).');
    if (Number(quantity) > selectedBatch.quantity) return setRowError(`Only ${selectedBatch.quantity} available in this batch.`);
    if (!reason) return setRowError('Select a return reason.');
    if (!selectedDistributorId && !selectedDistributorName.trim()) return setRowError('Select or enter a distributor.');

    setCart((prev) => [...prev, {
      id: `${selectedMedicine.medicine_id}-${selectedBatch.batch_number}-${Date.now()}`,
      medicine_id: selectedMedicine.medicine_id, medicine_name: selectedMedicine.name,
      batch_number: selectedBatch.batch_number, batch_quantity: selectedBatch.quantity,
      quantity: Number(quantity), reason, notes,
      distributor_id: selectedDistributorId, distributor_name: selectedDistributorName,
    }]);
    resetPicker();
  };

  const removeFromCart = (id) => setCart((prev) => prev.filter((c) => c.id !== id));

  const handleSubmitCart = async () => {
    setSubmitError('');
    if (cart.length === 0) return setSubmitError('Add at least one medicine to the return.');
    setSubmitting(true);
    try {
      const result = await apiFetch('/vendor-returns/bulk', {
        method: 'POST',
        body: JSON.stringify({
          items: cart.map((c) => ({
            medicine_id: c.medicine_id, batch_number: c.batch_number, quantity: c.quantity,
            distributor_id: c.distributor_id || undefined,
            distributor_name: c.distributor_id ? undefined : c.distributor_name,
            reason: c.reason, notes: c.notes,
          })),
          created_by: 'admin',
        }),
      });

      if (result.created && result.created.length > 0) {
        onCreated(result.created);
        notify(`Return raised · ${result.created_count} item${result.created_count > 1 ? 's' : ''}, stock deducted`, 'success');
      }
      if (result.failed && result.failed.length > 0) {
        const failedRowNumbers = new Set(result.failed.map((f) => f.row));
        setCart((prev) => prev.filter((_, idx) => failedRowNumbers.has(idx)));
        setSubmitError(result.failed.map((f) => `Row ${f.row + 1}: ${f.error}`).join(' · '));
      } else {
        resetAll();
      }
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const totalCartQty = cart.reduce((sum, c) => sum + c.quantity, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" keepMounted>
      <DialogTitle sx={{ bgcolor: COLORS.primary, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <AssignmentReturn />
          <Typography variant="h6" fontWeight={700}>Raise vendor return</Typography>
        </Stack>
      <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
    </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {cart.length > 0 && (
          <Paper variant="outlined" sx={{ mb: 2.5, borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 1.5, py: 1, bgcolor: COLORS.primaryBg }}>
              <Typography variant="caption" fontWeight={700} color={COLORS.primary} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {cart.length} item{cart.length > 1 ? 's' : ''} in this return · {totalCartQty} units total
              </Typography>
            </Box>
            <List disablePadding>
              {cart.map((c, i) => (
                <ListItem key={c.id} divider={i < cart.length - 1}
                  secondaryAction={<IconButton edge="end" size="small" color="error" onClick={() => removeFromCart(c.id)}><DeleteOutline fontSize="small" /></IconButton>}>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={600}>{c.medicine_name}</Typography>}
                    secondary={`Batch ${c.batch_number} · Qty ${c.quantity} · ${c.reason.replace('_', ' ')} · ${c.distributor_name || '—'}`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {!selectedMedicine && (
          <ToggleButtonGroup exclusive fullWidth size="small" value={mode} onChange={(e, v) => v && setMode(v)} sx={{ mb: 2 }}>
            <ToggleButton value="name">Search by name / batch code</ToggleButton>
            <ToggleButton value="batch"><QrCodeScanner sx={{ fontSize: 16, mr: 0.75 }} />Batch lookup</ToggleButton>
          </ToggleButtonGroup>
        )}

        {mode === 'batch' && !selectedMedicine && (
          <Box sx={{ mb: 2.5 }}>
            <TextField
              fullWidth size="small" label="Batch number / batch ID" placeholder="e.g. B-2026-01"
              value={batchQuery} onChange={(e) => setBatchQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            {batchSearching && <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Looking up batch…</Typography>}
            {batchError && <Alert severity="error" sx={{ mt: 1 }}>{batchError}</Alert>}
            {batchMatches.length > 1 && (
              <Paper variant="outlined" sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
                  Matches {batchMatches.length} medicines — pick the right one:
                </Typography>
                <List disablePadding>
                  {batchMatches.map((m) => (
                    <ListItemButton key={m.medicine.medicine_id} divider
                      onClick={() => { setSelectedMedicine(m.medicine); setSelectedBatch(m.matched_batch); setBatchMatches([]); }}>
                      <ListItemText
                        primary={m.medicine.name}
                        secondary={`${m.medicine.manufacturer || '—'} · batch ${m.matched_batch.batch_number} · qty ${m.matched_batch.quantity}`}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        )}

        {mode === 'name' && !selectedMedicine && (
          <Box sx={{ mb: 2.5, position: 'relative' }}>
            <TextField
              fullWidth size="small"
              label={cart.length > 0 ? `Search & add medicine #${cart.length + 1}` : 'Medicine name or batch code'}
              value={query} onChange={(e) => setQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            {searching && <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Searching…</Typography>}
            {medicines.length > 0 && (
              <Paper variant="outlined" sx={{ mt: 1, maxHeight: 320, overflowY: 'auto' }}>
                <List disablePadding>
                  {medicines.map((m) => (
                    <ListItemButton key={m.medicine_id} divider
                      onClick={() => { setSelectedMedicine(m); setMedicines([]); setQuery(''); }}
                      sx={{ display: 'block' }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Box>
                          <Typography variant="body2" fontWeight={700}>{m.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {m.manufacturer || 'Manufacturer —'}{m.examiner_salt ? ` · ${m.examiner_salt}` : ''}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight={700}>{money(m.price)}</Typography>
                          {m.base_price > 0 && <Typography variant="caption" color="text.secondary">BP {money(m.base_price)}</Typography>}
                        </Box>
                      </Stack>
                      <MedicineFactsChips m={m} compact />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        )}

        {selectedMedicine && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2, bgcolor: '#FAFAFA' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>{selectedMedicine.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedMedicine.manufacturer || 'Manufacturer —'}
                  {selectedMedicine.category ? ` · ${selectedMedicine.category}` : ''}
                </Typography>
              </Box>
              <IconButton size="small" onClick={resetPicker}><CloseIcon fontSize="small" /></IconButton>
            </Stack>

            {(selectedMedicine.price != null || selectedMedicine.stock != null) && (
              <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                {[['MRP', selectedMedicine.price != null ? money(selectedMedicine.price) : '—'],
                  ['Base price', selectedMedicine.base_price ? money(selectedMedicine.base_price) : '—'],
                  ['Selling price', selectedMedicine.discounted_price ? money(selectedMedicine.discounted_price) : '—'],
                  ['Total stock', selectedMedicine.stock ?? '—']].map(([label, value]) => (
                  <Grid item xs={3} key={label}>
                    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                    <Typography variant="body2" fontWeight={700}>{value}</Typography>
                  </Grid>
                ))}
              </Grid>
            )}
            <MedicineFactsChips m={selectedMedicine} />
          </Paper>
        )}

        {selectedMedicine && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Batch to return</Typography>
            {activeBatches.length === 0 ? (
              <Alert severity="error">No active batches with stock for this medicine.</Alert>
            ) : (
              <Stack spacing={1}>
                {activeBatches.map((b) => (
                  <Paper
                    key={b.batch_id || b.batch_number} variant="outlined"
                    onClick={() => setSelectedBatch(b)}
                    sx={{
                      p: 1.25, cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                      borderColor: selectedBatch?.batch_number === b.batch_number ? COLORS.primary : undefined,
                      bgcolor: selectedBatch?.batch_number === b.batch_number ? COLORS.primaryBg : undefined,
                    }}>
                    <Typography variant="body2" fontWeight={600}>{b.batch_number}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      qty {b.quantity} · MRP {money(b.mrp)} · exp {b.expiry_date || '—'} · BP {money(b.base_price)}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        )}

        {selectedMedicine && selectedBatch && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Distributor</Typography>
            <DistributorField
              distributors={distributors} loading={distributorsLoading}
              selectedId={selectedDistributorId} selectedName={selectedDistributorName}
              onSelect={(d) => { setSelectedDistributorId(d?.distributor_id || null); setSelectedDistributorName(d?.company_name || ''); }}
              onFreeText={(val) => { setSelectedDistributorId(null); setSelectedDistributorName(val); }}
              allDistributors={allDistributors}
              allDistributorsLoading={allDistributorsLoading}
            />
          </Box>
        )}

        {selectedMedicine && selectedBatch && (
          <>
            <Grid container spacing={2} sx={{ mb: 0.5 }}>
              <Grid item xs={6}>
                <TextField
                  sx={{ width: "250px" }} size="small" type="number" label="Quantity to return"
                  inputProps={{ min: 1, max: selectedBatch?.quantity || undefined }}
                  value={quantity} onChange={(e) => setQuantity(e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl sx={{ width: "250px" }} size="small" >
                  <InputLabel>Reason</InputLabel>
                  <Select label="Reason" value={reason} onChange={(e) => setReason(e.target.value)}>
                    {REASONS.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              No minimum — a single unit (qty 1) can be returned or later replaced on its own.
            </Typography>

            <TextField
              fullWidth multiline minRows={2} size="small" label="Notes (optional)"
              value={notes} onChange={(e) => setNotes(e.target.value)} sx={{ mb: 2 }}
            />

            {rowError && <Alert severity="error" sx={{ mb: 2 }}>{rowError}</Alert>}

            <Button
              fullWidth variant="outlined" startIcon={<AddIcon />} onClick={handleAddToCart}
              sx={{ mb: 1, borderColor: COLORS.primary, color: COLORS.primary, fontWeight: 600 }}
            >
              Add this medicine to the return
            </Button>
          </>
        )}

        {!selectedMedicine && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {cart.length === 0
              ? 'Search and add one or more medicines — any medicine, any reason, any quantity down to a single unit. Return several batches in one action.'
              : 'Keep searching to add as many medicines as this return needs — each one joins the cart above.'}
          </Typography>
        )}

        {submitError && <Alert severity="error" sx={{ mt: 1 }}>{submitError}</Alert>}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          fullWidth variant="contained" disabled={submitting || cart.length === 0} onClick={handleSubmitCart}
          sx={{ bgcolor: COLORS.primary, '&:hover': { bgcolor: COLORS.primaryDark }, py: 1.2, fontWeight: 700 }}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Inventory />}
        >
          {submitting ? 'Deducting stock…' : cart.length === 0 ? 'Add at least one medicine' : `Raise return · ${cart.length} item${cart.length > 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Settlement form for a single line item

function SettlementItemForm({ vendorReturn, item, onSettled, notify }) {
  const [type, setType] = useState('payment');
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('bank_transfer');
  const [reference, setReference] = useState('');
  const [replQty, setReplQty] = useState('');
  const [replBatch, setReplBatch] = useState('');
  const [replExpiry, setReplExpiry] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const outstanding = item.outstanding_value != null
    ? item.outstanding_value
    : Math.max(0, (item.line_value || 0) - ((item.replacement_value || 0) + (item.paid_amount || 0)));

  const submit = async () => {
    setError('');
    setSubmitting(true);
    try {
      let body;
      if (type === 'payment') {
        if (!amount || Number(amount) <= 0) throw new Error('Enter a valid amount.');
        body = {
          settlement_type: 'payment', item_id: item.item_id, amount: Number(amount),
          payment_mode: mode, payment_reference: reference, notes, recorded_by: 'admin',
        };
      } else {
        if (!replQty || Number(replQty) <= 0) throw new Error('Enter a valid replacement quantity (minimum 1 unit).');
        if (!replBatch.trim()) throw new Error('Replacement batch number is required.');
        body = {
          settlement_type: 'replacement', item_id: item.item_id, replacement_quantity: Number(replQty),
          replacement_batch_number: replBatch.trim(), replacement_expiry_date: replExpiry, notes, recorded_by: 'admin',
        };
      }
      const result = await apiFetch(`/vendor-returns/${vendorReturn.return_id}/settlements`, {
        method: 'POST', body: JSON.stringify(body),
      });
      onSettled(result.vendor_return, item.medicine_name);
      setAmount(''); setReference(''); setReplQty(''); setReplBatch(''); setReplExpiry(''); setNotes('');
      notify(`Settlement logged for ${item.medicine_name}`, 'success');
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (outstanding <= 0) {
    return (
      <Alert severity="success" icon={<CheckCircle fontSize="inherit" />} sx={{ fontWeight: 600 }}>
        Item fully settled.
      </Alert>
    );
  }

  return (
    <Box>
      <ToggleButtonGroup exclusive fullWidth size="small" value={type} onChange={(e, v) => v && setType(v)} sx={{ mb: 1.5 }}>
        <ToggleButton value="payment"><Payments sx={{ fontSize: 16, mr: 0.75 }} />Payment</ToggleButton>
        <ToggleButton value="replacement"><Inventory sx={{ fontSize: 16, mr: 0.75 }} />Replacement</ToggleButton>
      </ToggleButtonGroup>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Outstanding balance: <strong style={{ color: COLORS.error }}>{money(outstanding)}</strong>
      </Typography>

      {type === 'payment' ? (
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" type="number" label="Amount received"
              value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Up to ${outstanding.toFixed(2)}`}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Mode</InputLabel>
              <Select label="Mode" value={mode} onChange={(e) => setMode(e.target.value)}>
                {PAYMENT_MODES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="TXN / cheque no." />
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <TextField fullWidth size="small" type="number" label="Replacement qty" value={replQty} onChange={(e) => setReplQty(e.target.value)} placeholder="e.g. 1" />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="New batch number" value={replBatch} onChange={(e) => setReplBatch(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="New expiry date" value={replExpiry} onChange={(e) => setReplExpiry(e.target.value)} placeholder="YYYY-MM" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Adds on top of existing stock in this batch number — it never overwrites what's already there.
            </Typography>
          </Grid>
        </Grid>
      )}

      <TextField fullWidth size="small" label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} sx={{ mt: 1.5 }} />

      {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}

      <Button
        fullWidth variant="contained" disabled={submitting} onClick={submit}
        sx={{ mt: 2, bgcolor: COLORS.primary, '&:hover': { bgcolor: COLORS.primaryDark }, fontWeight: 700 }}
        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
      >
        {submitting ? 'Logging…' : 'Log settlement'}
      </Button>
    </Box>
  );
}
function ReturnSettlementForm({ vendorReturn, onSettled, notify }) {
  const items = vendorReturn.items || [];
  const outstandingItems = items.filter((i) => i.outstanding_value > 0);

  const [scope, setScope] = useState('whole'); // 'whole' | 'single'
  const [singleItemId, setSingleItemId] = useState('');
  const [type, setType] = useState('payment');

  // payment fields
  const [amount, setAmount] = useState('');
  const [paymentType, setPaymentType] = useState('received'); // NEW: 'received' | 'credit_note'
  const [mode, setMode] = useState('bank_transfer');
  const [reference, setReference] = useState('');

  // replacement fields — now an independent medicine search, not tied to returned item
  const [replMedicine, setReplMedicine] = useState(null);
  const [replQuery, setReplQuery] = useState('');
  const [replResults, setReplResults] = useState([]);
  const [replQty, setReplQty] = useState('');
  const [replBatch, setReplBatch] = useState('');
  const [replExpiry, setReplExpiry] = useState('');
  const [replBasePrice, setReplBasePrice] = useState('');

  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  function round2(n) { return Math.round(n * 100) / 100; }

const [replCart, setReplCart] = useState([]); // {id, medicine_id, medicine_name, quantity, batch_number, expiry_date, base_price, mrp}
const [distributorCreditBalance, setDistributorCreditBalance] = useState(0);

const totalOutstanding = scope === 'whole'
  ? outstandingItems.reduce((s, i) => s + i.outstanding_value, 0)
  : (items.find((i) => i.item_id === singleItemId)?.outstanding_value || 0);

const replCartTotal = replCart.reduce((s, c) => s + c.quantity * c.base_price, 0);
const excess = Math.max(0, round2(replCartTotal - totalOutstanding));
const coveredEstimate = Math.min(excess, distributorCreditBalance || 0);
const pendingEstimate = round2(excess - coveredEstimate);

const addReplToCart = () => {
  setError('');
  if (!replMedicine) return setError('Select a medicine first.');
  if (!replQty || Number(replQty) <= 0) return setError('Enter a valid quantity.');
  if (!replBatch.trim()) return setError('Batch number is required.');
  setReplCart((prev) => [...prev, {
    id: `${replMedicine.medicine_id}-${replBatch}-${Date.now()}`,
    medicine_id: replMedicine.medicine_id, medicine_name: replMedicine.name,
    quantity: Number(replQty), batch_number: replBatch.trim(), expiry_date: replExpiry,
    base_price: Number(replBasePrice) || replMedicine.base_price || 0,
    mrp: replMedicine.price,
  }]);
  setReplMedicine(null); setReplQty(''); setReplBatch(''); setReplExpiry(''); setReplBasePrice('');
};

const removeReplFromCart = (id) => setReplCart((prev) => prev.filter((c) => c.id !== id));

useEffect(() => {
  const distId = vendorReturn.distributor_id || items[0]?.distributor_id;
  if (!distId) return;
  apiFetch(`/distributors/${distId}/credit`)
    .then((d) => setDistributorCreditBalance(d.credit_balance || 0))
    .catch(() => setDistributorCreditBalance(0));
}, [vendorReturn.distributor_id]);

  // debounce medicine search for replacement
  useEffect(() => {
    if (!replQuery || replQuery.length < 2) { setReplResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const data = await apiFetch(`/medicines?search=${encodeURIComponent(replQuery)}&limit=10`, {}, MEDICINE_API_BASE);
        setReplResults(data.medicines || []);
      } catch { setReplResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [replQuery]);

  const submit = async () => {
    setError('');
    setSubmitting(true);
    try {
      let body = {
        settlement_type: type,
        recorded_by: 'admin',
        notes,
      };
      if (scope === 'single') body.item_id = singleItemId;
      else body.whole_return = true;

      if (type === 'payment') {
        if (!amount || Number(amount) <= 0) throw new Error('Enter a valid amount.');
        body = {
          ...body,
          amount: Number(amount),
          payment_type: paymentType,
          payment_mode: paymentType === 'received' ? mode : undefined,
          payment_reference: reference,
        };
      } else {
        if (replCart.length === 0) throw new Error('Add at least one medicine to the replacement.');
        body = {
          ...body,
          replacement_items: replCart.map((c) => ({
            medicine_id: c.medicine_id, quantity: c.quantity, batch_number: c.batch_number,
            expiry_date: c.expiry_date, base_price: c.base_price, mrp: c.mrp,
          })),
        };
      }
      const result = await apiFetch(`/vendor-returns/${vendorReturn.return_id}/settlements`, {
        method: 'POST', body: JSON.stringify(body),
      });
      onSettled(result.vendor_return);
      notify(`Settlement logged (${paymentType === 'credit_note' ? 'stored as credit' : 'recorded'})`, 'success');
      setAmount(''); setReference(''); setReplMedicine(null); setReplQty(''); setReplBatch(''); setReplExpiry(''); setReplBasePrice(''); setReplCart([]); setNotes('');
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (totalOutstanding <= 0 && outstandingItems.length === 0) {
    return <Alert severity="success">This return is fully settled.</Alert>;
  }

  return (
    <Box>
      {/* scope: whole return vs one medicine */}
      <ToggleButtonGroup exclusive fullWidth size="small" value={scope} onChange={(e, v) => v && setScope(v)} sx={{ mb: 1.5 }}>
        <ToggleButton value="whole">Settle whole return</ToggleButton>
        <ToggleButton value="single">Settle one medicine</ToggleButton>
      </ToggleButtonGroup>

      {scope === 'single' && (
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel>Medicine</InputLabel>
          <Select label="Medicine" value={singleItemId} onChange={(e) => setSingleItemId(e.target.value)}>
            {outstandingItems.map((i) => (
              <MenuItem key={i.item_id} value={i.item_id}>
                {i.medicine_name} — outstanding {money(i.outstanding_value)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Outstanding: <strong style={{ color: COLORS.error }}>{money(totalOutstanding)}</strong>
      </Typography>

      <ToggleButtonGroup exclusive fullWidth size="small" value={type} onChange={(e, v) => v && setType(v)} sx={{ mb: 1.5 }}>
        <ToggleButton value="payment"><Payments sx={{ fontSize: 16, mr: 0.75 }} />Payment</ToggleButton>
        <ToggleButton value="replacement"><Inventory sx={{ fontSize: 16, mr: 0.75 }} />Replacement</ToggleButton>
      </ToggleButtonGroup>

      {type === 'payment' ? (
        <>
          {/* NEW: received-now vs credit-note toggle */}
          <ToggleButtonGroup exclusive fullWidth size="small" value={paymentType} onChange={(e, v) => v && setPaymentType(v)} sx={{ mb: 1.5 }}>
            <ToggleButton value="received">Received now</ToggleButton>
            <ToggleButton value="credit_note">Store as credit for later purchase</ToggleButton>
          </ToggleButtonGroup>

          {paymentType === 'credit_note' && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              This amount will be added to the distributor's credit balance and can be applied to any future purchase order — no cash changes hands now.
            </Alert>
          )}

          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="number" label="Amount"
                value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder={`Up to ${totalOutstanding.toFixed(2)}`} />
            </Grid>
            {paymentType === 'received' && (
              <>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Mode</InputLabel>
                    <Select label="Mode" value={mode} onChange={(e) => setMode(e.target.value)}>
                      {PAYMENT_MODES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Reference" value={reference} onChange={(e) => setReference(e.target.value)} />
                </Grid>
              </>
            )}
          </Grid>
        </>
      ) : (
        <>
          {/* NEW: replacement medicine is a free search, not the returned item */}
          {!replMedicine ? (
            <Box sx={{ mb: 1.5 }}>
              <TextField
                fullWidth size="small" label="Search medicine to give as replacement"
                value={replQuery} onChange={(e) => setReplQuery(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
              {replResults.length > 0 && (
                <Paper variant="outlined" sx={{ mt: 1, maxHeight: 220, overflowY: 'auto' }}>
                  <List disablePadding>
                    {replResults.map((m) => (
                      <ListItemButton key={m.medicine_id} divider onClick={() => { setReplMedicine(m); setReplBasePrice(m.base_price || ''); setReplResults([]); setReplQuery(''); }}>
                        <ListItemText primary={m.name} secondary={`${m.manufacturer || '—'} · BP ${money(m.base_price)}`} />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
          ) : (
            <Paper variant="outlined" sx={{ p: 1.25, mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" fontWeight={600}>{replMedicine.name}</Typography>
              <IconButton size="small" onClick={() => setReplMedicine(null)}><CloseIcon fontSize="small" /></IconButton>
            </Paper>
          )}
      <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Quantity" value={replQty} onChange={(e) => setReplQty(e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="New batch number" value={replBatch} onChange={(e) => setReplBatch(e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Expiry (YYYY-MM)" value={replExpiry} onChange={(e) => setReplExpiry(e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Base price / unit" value={replBasePrice} onChange={(e) => setReplBasePrice(e.target.value)} />
            </Grid>
          </Grid>

          {replMedicine && (
            <Button
              fullWidth variant="outlined" size="small" startIcon={<AddIcon />} onClick={addReplToCart}
              sx={{ mt: 1.5, borderColor: COLORS.primary, color: COLORS.primary, fontWeight: 600 }}
            >
              Add this medicine to the replacement
            </Button>
          )}

          {replCart.length > 0 && (
            <Paper variant="outlined" sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, py: 1, bgcolor: COLORS.primaryBg }}>
                <Typography variant="caption" fontWeight={700} color={COLORS.primary} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {replCart.length} medicine{replCart.length > 1 ? 's' : ''} in this replacement · {money(replCartTotal)} total
                </Typography>
              </Box>
              <List disablePadding>
                {replCart.map((c, i) => (
                  <ListItem key={c.id} divider={i < replCart.length - 1}
                    secondaryAction={<IconButton edge="end" size="small" color="error" onClick={() => removeReplFromCart(c.id)}><DeleteOutline fontSize="small" /></IconButton>}>
                    <ListItemText
                      primary={<Typography variant="body2" fontWeight={600}>{c.medicine_name}</Typography>}
                      secondary={`Batch ${c.batch_number} · Qty ${c.quantity} · ${money(c.base_price)}/unit · ${money(c.quantity * c.base_price)}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          {replCart.length > 0 && excess > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              Replacement total ({money(replCartTotal)}) exceeds outstanding ({money(totalOutstanding)}) by {money(excess)}.
              {coveredEstimate > 0 && ` ${money(coveredEstimate)} will be auto-covered from the distributor's existing credit balance.`}
              {pendingEstimate > 0 && ` ${money(pendingEstimate)} will be recorded as pending — you can mark it paid later from the settlement history.`}
            </Alert>
          )}
        </>
      )}

      <TextField fullWidth size="small" label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} sx={{ mt: 1.5, mb: 2 }} />

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <Button fullWidth variant="contained" disabled={submitting || (type === 'replacement' && replCart.length === 0)} onClick={submit}
        sx={{ bgcolor: COLORS.primary, '&:hover': { bgcolor: COLORS.primaryDark }, fontWeight: 700 }}>
        {submitting ? 'Logging…' : 'Log settlement'}
      </Button>
    </Box>
  );
}


function DistributorCreditBadge({ distributorId }) {
  const [credit, setCredit] = useState(null);
  useEffect(() => {
    if (!distributorId) return;
    apiFetch(`/distributors/${distributorId}/credit`).then((d) => setCredit(d.credit_balance)).catch(() => {});
  }, [distributorId]);
  if (credit == null || credit <= 0) return null;
  return <Chip size="small" icon={<Payments sx={{ fontSize: 14 }} />} label={`Credit available: ${money(credit)}`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} />;
}


// Return detail dialog

function ReturnDetailDialog({ open, vendorReturn, onClose, onSettled, notify }) {
  if (!vendorReturn) return null;
  const meta = statusMeta(vendorReturn.settlement_status);
  const Icon = meta.icon;
  const items = vendorReturn.items && vendorReturn.items.length > 0 ? vendorReturn.items : [];
  const isMultiItem = items.length > 1;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ bgcolor: COLORS.primary, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h5" fontWeight={800} sx={{ fontFamily: 'monospace', letterSpacing: 0.5 }}>
              {vendorReturn.return_id}
            </Typography>
            <Chip size="small" icon={<Icon sx={{ fontSize: 14 }} />} label={meta.label}
              sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 700 }} />
          </Stack>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
            Raised {formatDateTime(vendorReturn.created_at)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
            {vendorReturn.medicine_name}{isMultiItem ? ` (${items.length} medicines)` : ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[['Items', items.length], ['Total quantity', vendorReturn.quantity],
            ['Distributor', vendorReturn.distributor_name],
            ['Reason', vendorReturn.reason === 'mixed' ? 'Mixed' : (vendorReturn.reason || '').replace('_', ' ')]].map(([label, value]) => (
            <Grid item xs={6} sm={3} key={label}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
              <Typography variant="body1" fontWeight={700} sx={{ textTransform: 'capitalize' }}>{value}</Typography>
            </Grid>
          ))}
        </Grid>

        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#FAFAFA' }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Return value (sum of line items)</Typography>
            <Typography variant="body2" fontWeight={700}>{money(vendorReturn.return_value)}</Typography>
          </Stack>
          <BalanceBar total={vendorReturn.return_value} settled={vendorReturn.settled_value || 0} />
        </Paper>



        <Typography variant="subtitle1" fontWeight={700} color={COLORS.primary} sx={{ mb: 1.5 }}>
          Settle this return
        </Typography>

        <ReturnSettlementForm vendorReturn={vendorReturn} onSettled={onSettled} notify={notify} />

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Per-medicine balance</Typography>
        {items.map((it) => (
          <Stack key={it.item_id} direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="body2">{it.medicine_name} · qty {it.quantity}</Typography>
            <Box sx={{ width: 160 }}><BalanceBar total={it.line_value} settled={it.settled_value || 0} height={6} /></Box>
          </Stack>
        ))}

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" fontWeight={700} color={COLORS.primary} sx={{ mb: 1.5 }}>
          Settlement history
        </Typography>
        {(!vendorReturn.settlements || vendorReturn.settlements.length === 0) ? (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">No settlements logged yet.</Typography>
        ) : (
          <List disablePadding>
            {vendorReturn.settlements.map((s, i) => {
              const relatedItem = items.find((it) => it.item_id === s.item_id);
              return (
                <ListItem key={s.settlement_id || i} alignItems="flex-start" divider={i < vendorReturn.settlements.length - 1}>
                  <ListItemIcon>
                    <Avatar sx={{
                      width: 32, height: 32,
                      bgcolor: s.settlement_type === 'payment' ? COLORS.warningBg : COLORS.infoBg,
                      color: s.settlement_type === 'payment' ? COLORS.warning : COLORS.info,
                    }}>
                      {s.settlement_type === 'payment' ? <Payments fontSize="small" /> : <Inventory fontSize="small" />}
                    </Avatar>
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={600}>
                        {s.settlement_type === 'payment'
                          ? `Payment — ${money(s.amount)}`
                          : `Replacement — ${s.replacement_quantity} units, batch ${s.replacement_batch_number}`}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="caption" color="text.secondary" component="span">
                          {s.settlement_type === 'payment'
                            ? `${s.payment_type === 'credit_note' ? 'Stored as distributor credit' : (s.payment_mode || '').replace('_', ' ')}`
                            : `→ ${s.replacement_medicine_name} · ${s.replacement_quantity} units, batch ${s.replacement_batch_number}`}
                          {' · '}{formatDateTime(s.created_at)}
                        </Typography>
                        {s.allocations?.length > 1 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Split across: {s.allocations.map((a) => {
                              const it = items.find((i) => i.item_id === a.item_id);
                              return `${it?.medicine_name || a.item_id} (${money(a.amount)})`;
                            }).join(', ')}
                          </Typography>
                        )}
                        {s.notes && <Typography variant="body2" sx={{ mt: 0.5 }}>{s.notes}</Typography>}
                      </>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderColor: COLORS.primary, color: COLORS.primary }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// Main dashboard

export default function VendorReturnsDashboard() {
  const [returns, setReturns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [quickAddBatch, setQuickAddBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const HEADER_DRAWER_WIDTH = 280;
  const notify = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const loadReturns = useCallback(async (currentFilter) => {
    const params = currentFilter && currentFilter !== 'all' ? `?settlement_status=${currentFilter}` : '';
    const data = await apiFetch(`/vendor-returns${params}`);
    setReturns(data.returns || []);
  }, []);

  const loadSummary = useCallback(async () => {
    const data = await apiFetch('/vendor-returns/summary');
    setSummary(data);
  }, []);

  const loadAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setErrorMsg('');
    try {
      await Promise.all([loadReturns(filter), loadSummary()]);
      if (isRefresh) notify('Dashboard refreshed', 'success');
    } catch (e) {
      setErrorMsg(e.message);
      notify('Could not refresh the dashboard', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter, loadReturns, loadSummary, notify]);

  useEffect(() => { loadAll(false); setPage(0);  }, [filter]);

  const handleCreated = async (createdReturns) => {
    setCreateOpen(false);
    setQuickAddBatch(null);
    if (createdReturns && createdReturns.length > 0) {
      setSelected(createdReturns[0]);
      setDetailOpen(true);
    }
    await loadAll(true);
  };


  const handleSettled = async (updatedReturn) => {
    setSelected(updatedReturn);
    setReturns((prev) => prev.map((r) => (r.return_id === updatedReturn.return_id ? updatedReturn : r)));
    await loadAll(true);
    if (updatedReturn.settlement_status === 'fully_settled') {
      notify(`Return ${updatedReturn.return_id} is now fully settled`, 'success');
    }
  };

  const handleRowClick = (r) => { setSelected(r); setDetailOpen(true); };

  const handleAddFromNearExpiry = (batchRow) => {
    setQuickAddBatch(batchRow);
    setCreateOpen(true);
  };

  const filters = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'partially_settled', label: 'Partial' },
    { value: 'fully_settled', label: 'Complete' },
  ];

  const paginated = returns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading && !refreshing) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50' }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ color: COLORS.primary, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">Loading vendor returns…</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
     sx={{
       minHeight: '100vh',
       bgcolor: 'grey.50',
       pb: 4,
       ml: { xs: 0, md: `${HEADER_DRAWER_WIDTH}px` },
       width: { xs: '100%', md: `calc(100% - ${HEADER_DRAWER_WIDTH}px)` },
     }}
   >
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      <Paper elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} flexWrap="wrap">
            <Box>
              <Typography variant="h3" fontWeight={700} color={COLORS.primary} gutterBottom>
                Vendor returns
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                Return &amp; settlement ledger — stock sent back to distributors
              </Typography>
              {errorMsg && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <WarningAmber sx={{ fontSize: 16, color: 'warning.main' }} />
                  <Typography variant="caption" color="warning.main">{errorMsg}</Typography>
                </Stack>
              )}
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="contained" startIcon={<AddIcon />}
                onClick={() => { setQuickAddBatch(null); setCreateOpen(true); }}
                sx={{ bgcolor: COLORS.primary, fontWeight: 700, '&:hover': { bgcolor: COLORS.primaryDark } }}
              >
                Raise return
              </Button>
              <Tooltip title="Refresh dashboard">
                <IconButton
                  onClick={() => loadAll(true)} disabled={refreshing}
                  sx={{
                    bgcolor: COLORS.primary, color: 'white', width: 48, height: 48,
                    '&:hover': { bgcolor: COLORS.primaryDark }, '&:disabled': { bgcolor: '#ccc' },
                    ...(refreshing && { '& svg': { animation: 'spin 1s linear infinite' } }),
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Container>
      </Paper>

      <Container maxWidth="xl">
        <NearExpiryPanel onAddToCart={handleAddFromNearExpiry} />

        {summary && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard title="Total returns" value={summary.total_returns} icon={<AssignmentReturn />} color={COLORS.primary} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard title="Pending settlement" value={summary.pending_settlement} icon={<ErrorOutline />} color={COLORS.warning} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard title="Partially settled" value={summary.partially_settled} icon={<Schedule />} color={COLORS.info} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard title="Fully settled" value={summary.fully_settled} icon={<CheckCircle />} color={COLORS.success} />
            </Grid>
            <Grid item xs={12} sm={12} md={2.4}>
              <StatCard title="Outstanding value" value={money(summary.total_outstanding_value)} icon={<ReceiptLong />} color={COLORS.error} />
            </Grid>
          </Grid>
        )}

        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 3, pb: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterList sx={{ color: COLORS.primary }} />
                <Typography variant="h6" fontWeight={700} color={COLORS.primary}>Return ledger</Typography>
              </Stack>
              <ButtonGroup size="small">
                {filters.map((f) => (
                  <Button
                    key={f.value}
                    variant={filter === f.value ? 'contained' : 'outlined'}
                    onClick={() => setFilter(f.value)}
                    sx={filter === f.value
                      ? { bgcolor: COLORS.primary, '&:hover': { bgcolor: COLORS.primaryDark } }
                      : { borderColor: COLORS.primary, color: COLORS.primary }}
                  >
                    {f.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Stack>
          </Box>

          <CardContent sx={{ p: 3 }}>
            {returns.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                <LocalShipping sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>No vendor returns yet</Typography>
                <Typography variant="body2">Raise a return to send expired, damaged, or excess stock back to a distributor.</Typography>
              </Box>
            ) : (
              <>
                <TableContainer sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: COLORS.primaryBg }}>
                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>Return ID</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>Medicines</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>Distributor</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>Reason</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>Progress</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: COLORS.primary }}>Raised</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: COLORS.primary }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginated.map((r) => {
                        const meta = statusMeta(r.settlement_status);
                        const Icon = meta.icon;
                        return (
                          <TableRow key={r.return_id} hover onClick={() => handleRowClick(r)} sx={{ cursor: 'pointer', '&:hover': { bgcolor: COLORS.primaryBg } }}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>{r.return_id}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>{r.medicine_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {r.item_count > 1 ? `${r.item_count} items · qty ${r.quantity}` : `Batch ${r.batch_number} · qty ${r.quantity}`}
                              </Typography>
                            </TableCell>
                            <TableCell><Typography variant="body2">{r.distributor_name}</Typography></TableCell>
                            <TableCell><ReasonChip reason={r.reason} /></TableCell>
                            <TableCell>
                              <Chip size="small" icon={<Icon sx={{ fontSize: 14 }} />} label={meta.label}
                                sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 700 }} />
                            </TableCell>
                            <TableCell sx={{ minWidth: 160 }}>
                              <BalanceBar total={r.return_value} settled={r.settled_value || 0} height={6} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">{formatDateTime(r.created_at)}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton size="small" sx={{ color: COLORS.primary }} onClick={(e) => { e.stopPropagation(); handleRowClick(r); }}>
                                <ArrowForward fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, returns.length)} of {returns.length} returns
                  </Typography>
                  <TablePagination
                    component="div" count={returns.length} page={page} onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                  />
                </Box>
              </>
            )}
          </CardContent>
        </Paper>
      </Container>

      <ReturnDetailDialog
        open={detailOpen}
        vendorReturn={selected}
        onClose={() => setDetailOpen(false)}
        onSettled={handleSettled}
        notify={notify}
      />

      <CreateReturnDialog
        open={createOpen}
        onClose={() => { setCreateOpen(false); setQuickAddBatch(null); }}
        onCreated={handleCreated}
        quickAddBatch={quickAddBatch}
        notify={notify}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}